import { useLanguage } from "../context/LanguageContext";
import { useState, useEffect } from "react";
import { fetchTranslationFile, getCachedTranslation, isTranslationCached } from "../api/languageService";
import { onTranslationsUpdated } from "../utils/translationRefresh";
import enTranslations from "../locals/en.json";
import kaTranslations from "../locals/ka.json";
import ruTranslations from "../locals/ru.json";

export const useTranslation = () => {
    const { language } = useLanguage();
    const [dynamicTranslations, setDynamicTranslations] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const getLanguageCode = (lang) => {
        switch (lang) {
            case "English": return "en";
            case "Georgian": return "ka";
            case "Russian": return "ru";
            default: return "en";
        }
    };

    useEffect(() => {
        if (language) {
            const languageCode = getLanguageCode(language);
            setIsLoading(true);
            fetchTranslationFile(languageCode)
                .then((translations) => {
                   
                    if (translations) {
                        setDynamicTranslations(translations);
                    } else {
                        // Fallback to static files if backend fails
                        setDynamicTranslations(null);
                    }
                })
                .catch((error) => {
                    console.error('Error loading dynamic translations:', error);
                    setDynamicTranslations(null);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [language]);

    // Listen for translation updates
    useEffect(() => {
        const cleanup = onTranslationsUpdated(() => {
            // Clear current translations to force reload
            setDynamicTranslations(null);
            
            // Reload translations for current language
            if (language) {
                const languageCode = getLanguageCode(language);
                setIsLoading(true);
                fetchTranslationFile(languageCode)
                    .then((translations) => {
                        if (translations) {
                            setDynamicTranslations(translations);
                        } else {
                            setDynamicTranslations(null);
                        }
                    })
                    .catch((error) => {
                        console.error('Error reloading translations:', error);
                        setDynamicTranslations(null);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        });

        return cleanup;
    }, [language]);

    const getBaseTranslations = () => {
        if (language === "English") return enTranslations;
        if (language === "Russian") return ruTranslations;
        return kaTranslations;
    };

    const mergeWithFallback = (fallbackValue, dynamicValue, namespace = null, key = null) => {
        if (dynamicValue === undefined || dynamicValue === null) {
            return fallbackValue;
        }

        const isObject = (val) =>
            typeof val === "object" && val !== null && !Array.isArray(val);

        if (isObject(fallbackValue) && isObject(dynamicValue)) {
            const merged = { ...fallbackValue };
            Object.keys(dynamicValue).forEach((currentKey) => {
                // Prioritize local translations for placeholders and labels to ensure latest updates
                if ((currentKey === "placeholders" || currentKey === "labels") && fallbackValue?.[currentKey]) {
                    merged[currentKey] = fallbackValue[currentKey];
                } else if (namespace === "subAdmin" && currentKey === "updatesuccess" && fallbackValue?.[currentKey]) {
                    // Prioritize local translation for subAdmin.updatesuccess to ensure correct update message
                    merged[currentKey] = fallbackValue[currentKey];
                } else if (namespace === "forgotPassword" && (currentKey === "resendIn" || currentKey === "seconds") && fallbackValue?.[currentKey]) {
                    // Prioritize local translation for forgotPassword.resendIn and seconds to avoid {{time}} placeholder issues
                    merged[currentKey] = fallbackValue[currentKey];
                } else {
                    merged[currentKey] = mergeWithFallback(fallbackValue?.[currentKey], dynamicValue[currentKey], namespace, currentKey);
                }
            });
            return merged;
        }

        return dynamicValue;
    };

    const t = (namespace) => {
        const baseTranslations = getBaseTranslations();
        const fallbackNamespace = baseTranslations[namespace];
        const dynamicNamespace = dynamicTranslations ? dynamicTranslations[namespace] : undefined;

        return mergeWithFallback(fallbackNamespace, dynamicNamespace, namespace);
    };

    return { t, isLoading };
};
